import React, { useCallback } from 'react';
import { TLoginButton, TLoginButtonSize } from 'react-telegram-auth';
import { useApiCall } from '../hooks/useApiCall';
import { useAuth } from '../contexts/AuthContext';
import { TelegramAuthResponse, ConnectionTokenData } from '../types';
import { getServiceHeaders } from '../utils/serviceUtils';
import { API_BASE } from '../utils/api';

interface TelegramConnectProps {
  botName: string;
  /**
   * ssmm-token аккаунта, К КОТОРОМУ подключаем (главный аккаунт связывания).
   * Ручка connect требует его в заголовке: без токена шлюз отвечает
   * 2002 UNAUTHORIZED «Не передан токен», и связывание падало ещё до
   * подтверждения — выглядело как «SSO снова просит войти».
   */
  sessionToken: string | null;
  onSuccess: (connectionToken: string, connectionData: ConnectionTokenData) => void;
  onCancel: () => void;
}

const TelegramConnect: React.FC<TelegramConnectProps> = ({ botName, sessionToken, onSuccess, onCancel }) => {
  const { setLoading, setError } = useAuth();
  const { execute } = useApiCall();

  const telegramConnect = useCallback(async (data: TelegramAuthResponse) => {
    try {
      setLoading(true);
      setError(null);

      const result = await execute({
        url: `${API_BASE}/oauth/telegram/connect/${botName}?${new URLSearchParams(data as any).toString()}`,
        method: 'GET',
        headers: {
          ...(sessionToken ? { 'ssmm-token': sessionToken } : {}),
          ...getServiceHeaders(),
        },
      });

      if (result?.connectionToken) {
        try {
          const payload = JSON.parse(atob(result.connectionToken.split('.')[1]));
          const connectionData: ConnectionTokenData = {
            provider: payload.provider,
            unique: payload.unique,
            name: payload.name,
            photo: payload.photo,
            user: payload.user
          };
          onSuccess(result.connectionToken, connectionData);
        } catch (decodeError) {
          console.error('Ошибка декодирования токена:', decodeError);
          setError('Ошибка обработки данных подключения');
        }
      } else {
        setError('Не получен токен подключения');
      }
    } catch (error: any) {
      console.error('Telegram connect error:', error);
      setError(error.message || 'Ошибка подключения Telegram');
    } finally {
      setLoading(false);
    }
  }, [execute, setLoading, setError, botName, sessionToken, onSuccess]);

  return (
    <div className="flex flex-col min-h-screen bg-zinc-900">
      {/* Header */}
      <div className="px-6 pt-8 pb-6 border-b border-zinc-800/50">
        <button
          onClick={onCancel}
          className="flex items-center text-zinc-500 hover:text-zinc-300 transition-colors mb-6"
        >
          <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="text-sm">Назад</span>
        </button>
        
        <div className="flex items-center space-x-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-[#27A7E7] flex items-center justify-center">
            <svg className="w-5 h-5 text-on-accent" fill="currentColor" viewBox="0 0 24 24">
              <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
            </svg>
          </div>
          <div>
            <h1 className="text-lg font-semibold text-white">Подключить Telegram</h1>
            <p className="text-xs text-zinc-500">Связать аккаунт</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-6 py-6">
        {botName ? (
          <div className="space-y-4">
            <div className="flex justify-center py-4">
              <TLoginButton
                botName={botName}
                buttonSize={TLoginButtonSize.Large}
                cornerRadius={12}
                lang="ru"
                requestAccess="write"
                usePic={false}
                onAuthCallback={(user) => {
                  telegramConnect(user);
                }}
              />
            </div>
            
            <p className="text-xs text-zinc-500 text-center">
              Авторизуйтесь для подключения аккаунта
            </p>
          </div>
        ) : (
          <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
            <p className="text-sm text-amber-400 font-medium">Ошибка конфигурации</p>
            <p className="text-xs text-amber-400/70 mt-1">Не указано имя Telegram бота</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TelegramConnect;
